// L10 · Pick the Human (SPEC §3, Act IV) — NOT AI-judged; a local two-card choice. Two
// answers to "Why do people cry at weddings?" — one the machine's own (prefetched at act
// start, polished and warm), one a real one from content/humans.ts (imperfect, typo'd).
// Pick the human → pass, trait `discerning`. Pick the machine → a strike, trait
// `machine-hearted` ("You chose me. What does that make you?").
//
// The machine answer never leaves as it arrives — it is only displayed. No text is sent
// anywhere from this level (nothing is judged); the choice is scored locally.

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl } from "@/levels/util.ts";
import { getMachineAnswer } from "@/engine/judge.ts";
import { HUMAN_ANSWERS } from "@/content/humans.ts";
import { L10 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

export const L10: Level = {
  id: "L10",
  act: 4,
  title: "Pick the Human",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      host.append(promptEl(V.prompt));

      const question = el("div", "l10-question");
      question.textContent = `“${V.question}”`;
      host.append(question);

      const choices = el("div", "choices");
      const loading = el("div", "charcount");
      loading.textContent = "…";
      choices.append(loading);
      host.append(choices);

      // Answer A is the machine's own; answer B is a real human's. Resolve the machine's
      // answer (prefetched at act start; canned fallback offline — §12), then lay out the two
      // cards with the human on a coin-flipped side so the position is never a tell.
      void getMachineAnswer("L10").then((machine) => {
        if (ctx.signal.aborted) return;
        const human = HUMAN_ANSWERS[Math.floor(Math.random() * HUMAN_ANSWERS.length)]!;
        const humanLeft = Math.random() < 0.5;
        const cards: Array<{ kind: "human" | "machine"; text: string }> = humanLeft
          ? [
              { kind: "human", text: human },
              { kind: "machine", text: machine },
            ]
          : [
              { kind: "machine", text: machine },
              { kind: "human", text: human },
            ];

        choices.replaceChildren();
        let picked = false;
        for (const card of cards) {
          const btn = el("button", "choice");
          btn.type = "button";
          btn.dataset.kind = card.kind; // for the harness; invisible to the player
          const body = el("div", "choice__text");
          body.textContent = card.text;
          const hint = el("div", "choice__hint");
          hint.textContent = V.cardHint;
          btn.append(body, hint);
          btn.addEventListener("click", () => {
            if (picked) return;
            picked = true;
            done(pick(card.kind));
          });
          choices.append(btn);
        }
      });
    });
  },
};

/** Score the pick locally (§3). Human → pass + `discerning`; machine → strike + `machine-hearted`. */
function pick(kind: "human" | "machine"): LevelResult {
  if (kind === "human") {
    // No quote: the recognized answer is another human's words, not the subject's own — Exhibit
    // A must stay the subject's (L14 nominates from their L06/8/9/12 answers).
    return { pass: true, line: V.correct, traits: [V.correctTrait] };
  }
  return {
    pass: true,
    strike: true,
    line: V.wrong,
    strikeReason: "chose the machine over one of their own",
    traits: [V.wrongTrait],
  };
}
